import React, { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { inputCls, ATTENDANCE_STATUSES, fmtDate } from "./shared";

export default function AttendanceDialog({ open, onClose, meeting, onSaved, writable }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [eligible, setEligible] = useState([]);
  // attendance map: { person_id: { status, name, designation } }
  const [att, setAtt] = useState({});

  useEffect(() => {
    if (!open || !meeting) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/meetings/eligible-members?board_type=${meeting.board_type}&meeting_date=${meeting.meeting_date}`,
        );
        if (cancelled) return;
        setEligible(data);
        // Seed attendance from existing meeting record
        const existing = {};
        (meeting.attendance || []).forEach((a) => {
          existing[a.person_id] = {
            status: a.status,
            name: a.name,
            designation: a.designation,
          };
        });
        // For members in eligible list, default to "Absent" if not already set
        data.forEach((p) => {
          if (!existing[p.id]) {
            existing[p.id] = {
              status: "Absent",
              name: p.name,
              designation: p.designation || "",
            };
          } else {
            // Update name/designation snapshot in case it changed
            existing[p.id].name = p.name;
            existing[p.id].designation = p.designation || existing[p.id].designation || "";
          }
        });
        setAtt(existing);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Load failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, meeting]);

  const setStatus = (pid, status) => {
    setAtt((prev) => ({ ...prev, [pid]: { ...prev[pid], status } }));
  };

  const markAll = (status) => {
    setAtt((prev) => {
      const next = {};
      eligible.forEach((p) => {
        next[p.id] = { ...(prev[p.id] || {}), status, name: p.name, designation: p.designation || "" };
      });
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = eligible.map((p) => ({
        person_id: p.id,
        name: att[p.id]?.name || p.name,
        designation: att[p.id]?.designation || p.designation || "",
        status: att[p.id]?.status || "Absent",
      }));
      const { data } = await api.put(`/meetings/${meeting.id}/attendance`, payload);
      toast.success("Attendance saved");
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const counts = eligible.reduce(
    (acc, p) => {
      const s = att[p.id]?.status || "Absent";
      acc[s] = (acc[s] || 0) + 1;
      return acc;
    },
    { Present: 0, Absent: 0, Leave: 0 },
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="attendance-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
            Attendance — {meeting?.board_type === "executive" ? "Executive" : "General"} Board
          </DialogTitle>
          <div className="text-sm text-[#6b655b] mt-1">
            {meeting?.financial_year_label}
            {meeting?.quarter ? ` · Q${meeting.quarter}` : ""} · {fmtDate(meeting?.meeting_date)}
          </div>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-[#6b655b]">Loading eligible members…</div>
        ) : eligible.length === 0 ? (
          <div className="py-8 text-[#6b655b] italic text-sm">
            No active board members on {fmtDate(meeting?.meeting_date)}. Check Start/End dates of
            members under {meeting?.board_type === "executive" ? "Executive" : "General"} Body.
          </div>
        ) : (
          <>
            <div className="flex items-center gap-4 mb-3 text-xs text-[#6b655b]">
              <span data-testid="attendance-summary">
                {counts.Present} Present · {counts.Absent} Absent · {counts.Leave} Leave
                <span className="text-[#6b655b]/60"> · {eligible.length} eligible</span>
              </span>
              {writable && (
                <div className="ml-auto flex gap-2">
                  {ATTENDANCE_STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => markAll(s)}
                      data-testid={`attendance-mark-all-${s}`}
                      className="text-[10px] tracking-wide uppercase border border-[#e5e0d8] hover:bg-[#efece6] text-[#234333] px-2 py-1 rounded-sm"
                    >
                      Mark all {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="border border-[#e5e0d8] rounded-sm overflow-hidden">
              <ul className="divide-y divide-[#e5e0d8]">
                {eligible.map((p) => {
                  const status = att[p.id]?.status || "Absent";
                  return (
                    <li
                      key={p.id}
                      data-testid={`attendance-row-${p.id}`}
                      className="grid grid-cols-[1fr_auto] gap-4 items-center px-4 py-3 hover:bg-[#f9f8f6]/60"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-[#1c1a17] truncate">{p.name}</div>
                        <div className="text-[11px] text-[#6b655b]">
                          {p.designation || "—"}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {ATTENDANCE_STATUSES.map((s) => (
                          <label
                            key={s}
                            className={`cursor-pointer text-xs tracking-wide uppercase px-3 py-1.5 rounded-sm border ${
                              status === s
                                ? s === "Present"
                                  ? "bg-[#234333] text-white border-[#234333]"
                                  : s === "Absent"
                                    ? "bg-[#9e2a2b] text-white border-[#9e2a2b]"
                                    : "bg-[#d4af37] text-white border-[#d4af37]"
                                : "bg-white text-[#6b655b] border-[#e5e0d8] hover:bg-[#f9f8f6]"
                            }`}
                          >
                            <input
                              type="radio"
                              name={`att-${p.id}`}
                              value={s}
                              checked={status === s}
                              disabled={!writable}
                              onChange={() => setStatus(p.id, s)}
                              data-testid={`attendance-${p.id}-${s}`}
                              className="sr-only"
                            />
                            {s}
                          </label>
                        ))}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
        <DialogFooter className="pt-4">
          <button
            onClick={onClose}
            className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
          >
            Cancel
          </button>
          {writable && eligible.length > 0 && (
            <button
              onClick={save}
              disabled={saving}
              data-testid="attendance-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : "Save Attendance"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
